import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Loader2, ArrowLeft, Send, FileText, FileSpreadsheet } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// R1-05 — Secure Report Email Sharing. One reusable dialog mounted from every report tab
// (Business/Products/NEEC MONEY/Purchasing pass a single fixed section; the Custom Report
// Builder passes whatever sections the Owner currently has selected). Always shows the
// recipient, computed subject and attachment names before anything is actually sent — the
// real send only happens after the Owner reviews that "confirm" step and clicks Send Email.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function EmailReportDialog({ open, onOpenChange, sections, range, groupBy = "sku", q = "", reportLabel }) {
  const { business } = useAuth();
  const [to, setTo] = useState("");
  const [formats, setFormats] = useState(["pdf"]);
  const [step, setStep] = useState("compose"); // "compose" | "confirm"
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, to } | { ok: false, error }

  const reset = () => { setTo(""); setFormats(["pdf"]); setStep("compose"); setResult(null); };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const toggleFormat = (f) => setFormats((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));

  const subjectPreview = `${reportLabel} — ${business?.name || "Business"} (${range.start} to ${range.end})`;
  // Mirrors the backend's exact filename sanitisation (server.py `fname_base` / `_CUSTOM_REPORT_SECTIONS[key]`
  // handling in POST /reports/custom/email) — only spaces and slashes are replaced, everything else
  // (e.g. "&", parentheses) is kept, so this preview matches the real attachment name exactly.
  const fileBase = reportLabel.replace(/ /g, "-").replace(/\//g, "-");
  const multi = sections.length > 1;
  const attachmentPreview = [
    ...(formats.includes("pdf") ? [`${fileBase}-${range.start}_to_${range.end}.pdf`] : []),
    ...(formats.includes("csv") ? [multi ? `${fileBase}-${range.start}_to_${range.end}.csv or .zip (one CSV per section with data)` : `${fileBase}-${range.start}_to_${range.end}.csv`] : []),
  ];

  const goToConfirm = () => {
    if (!EMAIL_RE.test(to.trim())) return toast.error("Enter a valid recipient email address");
    if (!formats.length) return toast.error("Choose at least one attachment format");
    setResult(null);
    setStep("confirm");
  };

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/reports/custom/email", {
        start: range.start, end: range.end, sections, group_by: groupBy, q, to: to.trim(), formats,
      });
      setResult({ ok: true, to: data.to });
      toast.success(`Report emailed to ${data.to}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "string" ? d : d?.message) || "Could not send the report — please try again";
      setResult({ ok: false, error: msg });
      toast.error(msg);
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md" data-testid="email-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-emerald-500" />Email Report</DialogTitle>
        </DialogHeader>

        {result?.ok ? (
          <div className="space-y-3 py-2" data-testid="email-report-success">
            <p className="text-sm">Sent to <strong data-testid="email-report-sent-to">{result.to}</strong>.</p>
            <p className="text-xs text-muted-foreground">If the recipient's mail server rejects it (bad address, full inbox), that bounce happens after this point and won't show here.</p>
          </div>
        ) : step === "compose" ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs mb-1.5 block">Recipient email</Label>
              <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="accountant@example.com" data-testid="email-report-to-input" />
              <p className="text-xs text-muted-foreground mt-1">Can be anyone — e.g. your accountant. Not limited to saved customer emails.</p>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Attach as</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={formats.includes("pdf") ? "default" : "outline"} onClick={() => toggleFormat("pdf")} data-testid="email-report-format-pdf"><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
                <Button type="button" size="sm" variant={formats.includes("csv") ? "default" : "outline"} onClick={() => toggleFormat("csv")} data-testid="email-report-format-csv"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />CSV</Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="email-report-period-summary">{reportLabel} · Period: {range.start} → {range.end}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="email-report-confirm">
            <div className="rounded-lg border border-border p-3 space-y-1.5 text-sm">
              <p><span className="text-muted-foreground">To:</span> <strong data-testid="email-report-confirm-to">{to.trim()}</strong></p>
              <p><span className="text-muted-foreground">Subject:</span> <span data-testid="email-report-confirm-subject">{subjectPreview}</span></p>
              <div>
                <span className="text-muted-foreground">Attachments:</span>
                <ul className="list-disc ml-5 mt-1" data-testid="email-report-confirm-attachments">
                  {attachmentPreview.map((f) => <li key={f} className="font-mono text-xs">{f}</li>)}
                </ul>
              </div>
            </div>
            {result?.error && <p className="text-xs text-rose-500" data-testid="email-report-error">{result.error}</p>}
            <p className="text-xs text-muted-foreground">Double-check the recipient above — Send Email delivers the real report now.</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {result?.ok ? (
            <Button onClick={close} data-testid="email-report-done-btn">Done</Button>
          ) : step === "compose" ? (
            <>
              <Button variant="ghost" onClick={close} data-testid="email-report-cancel-btn">Cancel</Button>
              <Button onClick={goToConfirm} data-testid="email-report-review-btn">Review &amp; Send</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => { setResult(null); setStep("compose"); }} disabled={sending} data-testid="email-report-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
              <Button onClick={send} disabled={sending} data-testid="email-report-send-btn">{sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}Send Email</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
